import type { GrammarTeaching } from "../content/lianxi/yufa-teaching.js";
import { teachingOn } from "./lianxi-yufa-teaching.js";
import { inRow, onLine } from "./fuxi-tile-moves.js";
import type { ItemAnswer } from "../fuxi/yufa-marking.js";
import { said } from "./xiezi-parts.js";
import { arrangementOrders } from "../fuxi/arrangement-orders.js";
import {
  choiceHolder,
  focusedGroup,
  focusedAnswer,
} from "./lianxi-yufa-focused.js";
import { readRepair } from "./lianxi-yufa-repair.js";

/**
 * Reading one item of a grammar exercise 语法练习 off its form.
 *
 * `partials/lianxi/yufa.html` draws each item as a form marked with
 * `data-cb-yufa-` names, and this is the one place those names are read. A
 * gap item carries its input and the word on it. An arrangement carries the
 * tiles a word-order card has, each naming its place in the sentence, and
 * the line the reader puts them on. A Choose item carries its sentences as
 * radio buttons and the right one's place on the group, and a Transform item
 * its field with every right rewrite on it. `lianxi-yufa.ts` asks for the answer,
 * and `lianxi-yufa-item-view.ts` puts the item back and says what the answer
 * was.
 *
 * **A part the markup left out is an error, not a branch.** The block is
 * drawn by one template and every item carries every part, so a missing one
 * is a markup bug and is said so, rather than guarded against at each use.
 * `lianxi-yufa-start.ts` is the one place that checks and leaves a block
 * alone.
 */

/** One part of the item, by its `data-cb-yufa-` name, where it has one. */
export function yufaPart(
  within: Element,
  name: string,
): HTMLElement | undefined {
  const found = within.querySelector(`[data-cb-yufa-${name}]`);

  return found instanceof HTMLElement ? found : undefined;
}

/**
 * One part of the item, by its `data-cb-yufa-` name.
 * @param within - The item.
 * @param name - The part's name.
 * @returns The part.
 * @throws Error - Where the markup drew no such part.
 */
export function requirePart(within: Element, name: string): HTMLElement {
  const found = yufaPart(within, name);

  if (found === undefined) {
    throw new Error(`The item has no ${name} part.`);
  }

  return found;
}

/** The field a gap is typed into. */
export function typedInput(form: HTMLFormElement): HTMLInputElement {
  const found = requirePart(form, "typed");

  if (!(found instanceof HTMLInputElement)) {
    throw new TypeError("The gap's typed part is not a field.");
  }

  return found;
}

/** Whether the item is an arrangement. */
export function isArrangement(form: HTMLFormElement): boolean {
  return form.dataset["cbYufaKind"] === "arrange";
}

/** Whether the item is a Choose item, answered by picking a sentence. */
export function isChoice(form: HTMLFormElement): boolean {
  return form.dataset["cbYufaKind"] === "choose";
}

/**
 * The options of an item answered by choosing one, as their radio buttons in
 * the order shown: a Choose item's sentences, a contrast's words, a meaning's
 * answers or a placement's slots. None for any other item.
 */
export function optionsOf(form: HTMLFormElement): readonly HTMLInputElement[] {
  return [...form.querySelectorAll<HTMLInputElement>("[data-cb-yufa-option]")];
}

/** Whether the item is answered by choosing one of its option rows. */
export function isChoosing(form: HTMLFormElement): boolean {
  return optionsOf(form).length > 0;
}

/** Lines of text an attribute holds one to a line, or none. */
function linesOf(element: HTMLElement, name: string): readonly string[] {
  return said(element, name)
    .split("\n")
    .filter((line) => line !== "");
}

/** The sentence a Choose item's reader picked, against the right one. */
function chosenAnswer(form: HTMLFormElement): ItemAnswer | undefined {
  const picked = optionsOf(form).find((option) => option.checked);

  return picked === undefined
    ? undefined
    : {
        kind: "choose",
        chosen: Number(picked.value),
        right: Number(said(requirePart(form, "options"), "cbYufaRight")),
        options: optionsOf(form).map(
          (option) => option.dataset["cbYufaText"] ?? "",
        ),
        ...taught(form),
      };
}

/** What a gap's or a Transform item's reader typed, against what it wants. */
function typedAnswer(form: HTMLFormElement): ItemAnswer | undefined {
  const input = typedInput(form);
  const typed = input.value.trim();

  if (typed === "") {
    return undefined;
  }

  return form.dataset["cbYufaKind"] === "transform"
    ? {
        kind: "transform",
        ...taught(form),
        typed,
        accepted: linesOf(input, "cbYufaAccepted"),
        other: linesOf(input, "cbYufaOther"),
      }
    : {
        kind: "gap",
        typed,
        word: said(input, "cbYufaAnswer"),
        other: said(input, "cbYufaOther"),
      };
}

/** A tile's characters, which are the 简体 span it carries. */
function pieceOf(tile: Element): string {
  return tile.querySelector('[lang="zh-Hans-CN"]')?.textContent?.trim() ?? "";
}

/**
 * What the reader has answered on the item, or nothing where they have
 * answered nothing yet.
 *
 * A placed tile carries the spans of the tile it came from, which is what
 * `place` in `./fuxi-tile-moves.ts` copies onto the line, so the order is
 * read off the line itself.
 * @param form - The item.
 * @returns The answer, with what it is marked against.
 */
export function readAnswer(form: HTMLFormElement): ItemAnswer | undefined {
  if (form.dataset["cbYufaKind"] === "repair") {
    return readRepair(form);
  }
  if (choiceHolder(form) !== null || focusedGroup(form) !== null) {
    return focusedAnswer(form);
  }
  if (isChoice(form)) {
    return chosenAnswer(form);
  }

  if (!isArrangement(form)) {
    return typedAnswer(form);
  }

  const pieces = [...form.querySelectorAll<HTMLElement>(inRow)]
    .toSorted(
      (one, other) =>
        Number(one.dataset["cbYufaPlace"]) -
        Number(other.dataset["cbYufaPlace"]),
    )
    .map((tile) => pieceOf(tile));
  const arranged = [...form.querySelectorAll(onLine)].map((placed) =>
    pieceOf(placed),
  );
  const orders = form.querySelector("[data-cb-yufa-orders]");
  const accepted = arrangementOrders(
    JSON.parse(orders?.getAttribute("data-cb-yufa-orders") ?? "[]") as unknown,
    pieces.length,
  );

  if (accepted === undefined) {
    throw new Error("The item's checked orders do not use every tile once.");
  }

  return arranged.length === 0
    ? undefined
    : { kind: "arrange", pieces, arranged, accepted };
}

function taught(form: HTMLFormElement): { teaching?: GrammarTeaching } {
  const teaching = teachingOn(form);
  return teaching === undefined ? {} : { teaching };
}
